import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { propType } from 'graphql-anywhere';
import { useHistory } from 'react-router-dom';

import { DokkioButton, DokkioDialog } from '../../ui';

import { SimpleForm } from '../../forms';

import dokkioLabelFragment from '../../fragments/dokkioLabelFragment';

import MUTATION_MERGE_DOKKIO_LABELS from '../../queries/mutationMergeDokkioLabels';

import { StatusMessageContext } from '../../contexts/StatusMessageContext';

import withData from '../../decorators/withData';

import composeWithDokkioDialogComponents from '../../utils/composeWithDokkioDialogComponents';

import { DataContext } from '../Data';
import DokkioLabelInput from '../DokkioLabelInput';
import ErrorMessage from '../ErrorMessage';

export const MergeDokkioLabelDialog = ({
  dokkioLabel: secondaryDokkioLabel,
  onClose,
  onSuccess,
  open,
}) => {
  const { dokkioLabelByID, integrationByID, mergeDokkioLabels } = useContext(DataContext);
  const { setStatusMessage } = useContext(StatusMessageContext);
  const { push } = useHistory();

  const onSubmit = async ({ primaryDokkioLabelID }) => {
    await mergeDokkioLabels({
      variables: {
        primaryDokkioLabelID,
        secondaryDokkioLabelID: secondaryDokkioLabel.id,
      },
    });
    const primaryDokkioLabel = dokkioLabelByID[primaryDokkioLabelID];
    setStatusMessage({
      label: `Merged "${secondaryDokkioLabel.name}" into "${primaryDokkioLabel.name}"`,
      type: 'success',
    });
    await onSuccess();
    push(`/a/${primaryDokkioLabel.accountID}/dokkioLabels/${primaryDokkioLabel.id}`);
  };

  const getInfoMessage = (primaryDokkioLabelID) => {
    const primaryDokkioLabel = dokkioLabelByID[primaryDokkioLabelID];
    if (!primaryDokkioLabel) {
      return null;
    }

    return `
      All current ${secondaryDokkioLabel.namePlural} will become ${primaryDokkioLabel.namePlural}.
      Any ${secondaryDokkioLabel.namePlural} that share a name with a ${primaryDokkioLabel.nameSingular}
      will be merged into the ${primaryDokkioLabel.nameSingular}. All information about the label
      "${secondaryDokkioLabel.name}" will be deleted, including its icon, description,
      and label type.`;
  };

  const getOptionDisabledReason = ({ id, integrationID }) => {
    if (id === secondaryDokkioLabel.id) {
      return 'Cannot merge label with itself';
    }
    if (integrationID) {
      const { integrationType } = integrationByID[integrationID];
      return `Cannot merge with labels from a ${integrationType} content source`;
    }
    return null;
  };

  return (
    <DokkioDialog
      onClose={onClose}
      open={open}
    >
      <SimpleForm onSubmit={onSubmit}>
        {({ formError, getFieldValue, isSubmitting }) => (
          <React.Fragment>
            <DokkioDialog.Body title={`Merge Label "${secondaryDokkioLabel.name}" into...`}>
              <ErrorMessage errors={[formError]} />
              <DokkioLabelInput
                displayName="Label to merge into"
                name="primaryDokkioLabelID"
                getOptionDisabledReason={getOptionDisabledReason}
                infoMessage={getInfoMessage(getFieldValue('primaryDokkioLabelID'))}
                required
                showNewOption={false}
                showNoneOption={false}
              />
            </DokkioDialog.Body>
            <DokkioDialog.Footer>
              <DokkioButton
                buttonColor="white"
                disabled={isSubmitting}
                onClick={onClose}
                type="button"
              >
                Cancel
              </DokkioButton>
              <DokkioButton
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Merging...' : 'Merge'}
              </DokkioButton>
            </DokkioDialog.Footer>
          </React.Fragment>
        )}
      </SimpleForm>
    </DokkioDialog>
  );
};

MergeDokkioLabelDialog.propTypes = {
  dokkioLabel: propType(dokkioLabelFragment).isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  open: PropTypes.bool.isRequired,
};

MergeDokkioLabelDialog.defaultProps = {
  onSuccess: () => null,
};

export default composeWithDokkioDialogComponents(
  withData({
    mutations: [MUTATION_MERGE_DOKKIO_LABELS],
  }),
)(MergeDokkioLabelDialog);
